import { useState, useEffect } from 'react';
import { Button, Card, Space, Switch, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, DownloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector} from 'react-redux';
import axios from 'axios';
import queryString from 'query-string';
import { APIClient } from '../../helpers/api_helper';
import IntentsTable from '../../Common/IntentsTable';
import IntentDrawer from '../../Common/IntentDrawer';
import ConfirmModal from '../../Common/ConfirmModal';
import ProjectIntentsByCategoryView from './ProjectIntentsByCategoryView';
import utils from '../../Common/utils';
import { setInitialFilterConf, setFilterData, updateFilterData } from '../../slices/filters/thunk';

const api = new APIClient();


const ProjectIntentsTab = ({ projData, isActive, onVersionCreated }) => {

  const dispatch = useDispatch();
  const filterdata = useSelector((state) => state.Filterdata.data)
  const [intentsData, setIntentsData] = useState({});
  const [groupedData, setGroupedData] = useState([]);
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [editedIntentId, setEditedIntentId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [massEditOpen, setMassEditOpen] = useState(false);
  const [showDeletionConfirmModal, setShowDeletionConfirmModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [loading, setLoading] = useState(false);


  const loadIntents = (f) => {
    const params = utils.cleanEmpty({
      ...f,
      projects: [projData.id],
    });
    setLoading(true);
    api.get('/api/intents/', params)
      .then(resp => setIntentsData(resp))
      .finally(() => setLoading(false));
  };

  const loadGroupedIntents = (f) => {
    const params = utils.cleanEmpty({
      ...f,
      projects: [projData.id],
    });
    setLoading(true);
    api.get('/api/intents/grouped_by_category/', params)
      .then(resp => setGroupedData(resp || []))
      .finally(() => setLoading(false));
  };

  const reload = () => {
    if (!projData.id) return;
    if (groupByCategory) {
      loadGroupedIntents(filterdata);
    } else {
      loadIntents(filterdata);
    }
  };

  const handleExport = () => {
    const scope = selectedIds.length > 0
      ? { ids: selectedIds.join(',') }
      : { ...filterdata, projects: [projData.id] };
    const qs = queryString.stringify(utils.cleanEmpty(scope));
    setExportLoading(true);
    axios.get(`/api/intents/export_xlsx/?${qs}`, { responseType: 'blob' })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'intents.xlsx';
        a.click();
        URL.revokeObjectURL(url);
      })
      .finally(() => setExportLoading(false));
  };

  const handleConfirmedDelete = () => {
    api.delete('/api/intents/delete_bulk/', { ids: selectedIds }, { successMsg: `Uspešno izbrisanih ${selectedIds.length} transakcij` })
      .then(() => {
        setShowDeletionConfirmModal(false);
        setSelectedIds([]);
        reload();
      })
      .catch(error => {
        console.error(error);
      });
  };

  useEffect(() => {
    if (!isActive) return;
    dispatch(setInitialFilterConf({
      data: {
        'date_range': null,
      },
      hidden: ['projects'],
      disabled: ['projects'],
    }))
    dispatch(setFilterData(utils.getObjectFromURL()))
  }, [isActive]);

  useEffect(() => {
    if (!projData.id) return;
    if (groupByCategory) {
      loadGroupedIntents(filterdata);
    } else {
      loadIntents(filterdata);
    }
  }, [projData.id, filterdata, groupByCategory]);

  return (
    <>
      <IntentDrawer
        open={drawerOpen}
        editedIntentId={editedIntentId}
        defaultValues={{ project: projData.id }}
        onDone={() => { setDrawerOpen(false); reload(); }}
        onCancel={() => setDrawerOpen(false)}
        onVersionCreated={onVersionCreated}
      />
      <IntentDrawer
        open={massEditOpen}
        massEdit={true}
        massEditIds={selectedIds}
        onCancel={() => setMassEditOpen(false)}
        onDone={() => {
          setMassEditOpen(false);
          setSelectedIds([]);
          reload();
        }}
        editedIntentId={null}
      />
      <div style={{ marginBottom: 12 }}>
        <Space>
          <span>Prikaži po kategorijah</span>
          <Switch
            checked={groupByCategory}
            onChange={(checked) => setGroupByCategory(checked)}
          />
        </Space>
      </div>
      <Card
        extra={
          <Space>
            <Tooltip title="Množično urejanje">
              <Button
                disabled={selectedIds.length === 0}
                onClick={() => setMassEditOpen(true)}
                icon={<EditOutlined />}
              />
            </Tooltip>
            <Tooltip title="Izbriši izbrane">
              <Button
                disabled={selectedIds.length === 0}
                onClick={() => setShowDeletionConfirmModal(true)}
                icon={<DeleteOutlined />}
              />
            </Tooltip>
            <Tooltip title="Izvozi v Excel">
              <Button
                loading={exportLoading}
                disabled={exportLoading}
                onClick={handleExport}
                icon={<DownloadOutlined />}
              />
            </Tooltip>
            <Tooltip title="Dodaj transakcijo">
              <Button
                onClick={() => {
                  setEditedIntentId(null);
                  setDrawerOpen(true);
                }}
                icon={<PlusOutlined />}
              />
            </Tooltip>
          </Space>
        }
      >
        {groupByCategory ? (
          <ProjectIntentsByCategoryView
            groupedData={groupedData}
            loading={loading}
            onIntentEdit={intent => { setEditedIntentId(intent.id); setDrawerOpen(true); }}
          />
        ) : (
          <IntentsTable
            intentsData={intentsData}
            loading={loading}
            page={filterdata.page}
            onPageChange={p => dispatch(updateFilterData({page: p}))}
            onIntentEdit={intent => { setEditedIntentId(intent.id); setDrawerOpen(true); }}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        )}
      </Card>
      <ConfirmModal
        textComponent={<p>{`Ali ste prepričani, da želite izbrisati ${selectedIds.length} transakcij?`}</p>}
        open={showDeletionConfirmModal}
        confirmBtnTitle='Izbriši'
        confirmBtnProps={{ danger: true }}
        onOk={() => handleConfirmedDelete()}
        onCancel={() => setShowDeletionConfirmModal(false)}
      />
    </>
  );
};

export default ProjectIntentsTab;
